<script setup>
import { ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'subject-added'])

const subjectName = ref('')
const loading = ref(false)
const error = ref('')
const success = ref('')

// Reset form when modal is opened/closed
watch(() => props.show, (newVal) => {
  if (newVal) {
    subjectName.value = ''
    error.value = ''
    success.value = ''
  }
})

const submitSubject = async () => {
  if (!subjectName.value.trim()) {
    error.value = 'Please enter a subject name'
    return
  }

  loading.value = true
  error.value = ''
  success.value = ''

  try {
    const response = await fetch('/become-a-tutor/add-subject', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        name: subjectName.value.trim()
      })
    })

    const data = await response.json()

    if (!response.ok) {
      // Handle validation errors or other errors
      if (data.message) {
        error.value = data.message
      } else if (data.errors && data.errors.name) {
        error.value = data.errors.name[0]
      } else {
        error.value = 'Failed to create subject. Please try again.'
      }
      loading.value = false
      return
    }

    // Success - emit the new subject
    success.value = 'Subject added successfully!'
    loading.value = false

    setTimeout(() => {
      emit('subject-added', data.subject)
      emit('close')
    }, 500)

  } catch (err) {
    error.value = 'An error occurred. Please try again.'
    loading.value = false
  }
}

const closeModal = () => {
  if (!loading.value) {
    emit('close')
  }
}
</script>

<template>
  <!-- Modal Overlay -->
  <Transition name="modal">
    <div
      v-if="show"
      class="fixed inset-0 z-[60] overflow-y-auto"
      @click.self="closeModal"
    >
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black bg-opacity-30 transition-opacity"></div>

      <!-- Modal Container -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div
          class="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl transform transition-all"
          @click.stop
        >
          <!-- Modal Header with Icon -->
          <div class="relative p-8 pb-6">
            <button
              type="button"
              @click="closeModal"
              :disabled="loading"
              class="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 hover:bg-gray-100 rounded-full p-2"
            >
              <span class="material-symbols-outlined text-2xl">close</span>
            </button>

            <div class="flex items-start space-x-4">
              <div class="flex-shrink-0">
                <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span class="material-symbols-outlined text-white text-4xl">school</span>
                </div>
              </div>
              <div class="flex-1 pt-2">
                <h3 class="text-3xl font-bold text-gray-900 mb-2">Add New Subject</h3>
                <p class="text-gray-600 text-base">
                  Can't find the subject you teach? Add it to our platform and we'll make it available for tutoring.
                </p>
              </div>
            </div>
          </div>

          <!-- Modal Body -->
          <div class="px-8 pb-8">
            <!-- Success Message -->
            <div
              v-if="success"
              class="mb-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl flex items-center"
            >
              <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
                <span class="material-symbols-outlined text-white text-xl">check</span>
              </div>
              <div>
                <p class="text-green-900 font-semibold">Success!</p>
                <p class="text-green-700 text-sm">{{ success }}</p>
              </div>
            </div>

            <!-- Error Message -->
            <div
              v-if="error"
              class="mb-6 p-5 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-2xl flex items-center"
            >
              <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
                <span class="material-symbols-outlined text-white text-xl">error</span>
              </div>
              <div>
                <p class="text-red-900 font-semibold">Unable to Add Subject</p>
                <p class="text-red-700 text-sm">{{ error }}</p>
              </div>
            </div>

            <!-- Form -->
            <form @submit.prevent="submitSubject" class="space-y-6">
              <!-- Info Box -->
              <div class="bg-blue-50 border-2 border-blue-100 rounded-2xl p-5">
                <div class="flex items-start space-x-3">
                  <span class="material-symbols-outlined text-blue-600 text-2xl flex-shrink-0">info</span>
                  <div>
                    <p class="text-blue-900 font-semibold text-sm mb-1">What happens next?</p>
                    <p class="text-blue-700 text-sm">
                      Your subject will be automatically added to the platform and available for selection immediately. We'll assign it a unique color and icon.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-base font-bold text-gray-900 mb-3">
                  Subject Name <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <div class="absolute left-4 top-1/2 transform -translate-y-1/2">
                    <span class="material-symbols-outlined text-gray-400 text-2xl">subject</span>
                  </div>
                  <input
                    v-model="subjectName"
                    type="text"
                    required
                    placeholder="e.g., Advanced Mathematics, Computer Science, Biology"
                    :disabled="loading"
                    :class="[
                      'w-full pl-14 pr-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue outline-none transition-all text-gray-900 text-base disabled:opacity-50 disabled:bg-gray-50',
                      error ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-transparent'
                    ]"
                    @input="error = ''"
                  />
                </div>
                <p class="text-sm text-gray-500 mt-2 flex items-center">
                  <span class="material-symbols-outlined text-xs mr-1">lightbulb</span>
                  Use the full subject name for better searchability
                </p>
              </div>

              <!-- Modal Footer -->
              <div class="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t-2 border-gray-100">
                <p class="text-sm text-gray-500 flex self-start items-center">
                  <span class="material-symbols-outlined text-base mr-1">lock</span>
                  Your information is secure
                </p>
                <div class="flex space-x-3">
                  <button
                    type="button"
                    @click="closeModal"
                    :disabled="loading"
                    class="px-6 py-3.5 text-gray-700 font-semibold bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    :disabled="loading || !subjectName.trim()"
                    class="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg shadow-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center"
                  >
                    <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
                    <span v-else class="material-symbols-outlined mr-2 text-xl">add_circle</span>
                    {{ loading ? 'Adding Subject...' : 'Add Subject' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}
</style>
